"use client";

import { MoreHorizontal, Trash2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/components/providers/auth-context";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteQuestionModal } from "@/features/questions/components/delete-question-modal";
import { useI18n } from "@/locales/client";

type QuestionDropdownProps = {
  studySetId: string;
  questionId: string;
  questionUserId: string;
  onDeleteSuccess: () => void;
};

export const QuestionDropdown = ({
  studySetId,
  questionId,
  questionUserId,
  onDeleteSuccess,
}: QuestionDropdownProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const t = useI18n();
  const { userId } = useAuth();

  // Only show dropdown if user is the owner of the question
  if (!userId || userId !== questionUserId) {
    return null;
  }

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">More options</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={handleDeleteClick}
            className="text-destructive focus:text-destructive cursor-pointer"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("questionActions.delete.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteQuestionModal
        studySetId={studySetId}
        questionId={questionId}
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        onDelete={onDeleteSuccess}
      />
    </>
  );
};
